import { ChevronRight, Loader2 } from 'lucide-react';
import { cn } from '@ai-tutor/ui';
import { InlineMarkdown } from '@/components/core/markdown/markdown';
import type { SectionPodcastSummary } from '@/lib/api-client';
import { isPodcastInFlight } from '@/hooks/lesson/useSectionPodcasts';
import { LessonPanelHeader } from '../media/TextLessonFrame';
import { SectionListenButton } from './SectionListenButton';

function statusLine(s: SectionPodcastSummary): string {
  if (s.status === 'completed') {
    return `${Math.max(1, Math.round(s.duration / 60))} min`;
  }
  if (isPodcastInFlight(s.status)) return 'Being recorded · about a minute';
  if (s.status === 'failed') return "Didn't finish · try again";
  if (s.locked) return 'Plus';
  const n = s.concepts.length;
  return n === 1 ? '1 concept' : `${n} concepts`;
}

/**
 * The Podcast tab: one row per section of the lesson, each with its own
 * podcast - made on demand, a unit of the podcast quota each. A made one
 * opens its player and transcript; the button on the right makes it, or
 * plays it in the app's player without leaving the list.
 */
export function SectionPodcastList({
  lessonId,
  lessonTitle,
  sections,
  onOpen,
}: {
  lessonId: string;
  lessonTitle: string;
  sections: SectionPodcastSummary[];
  onOpen: (index: number) => void;
}) {
  const made = sections.filter(s => s.status === 'completed').length;
  const anyLocked = sections.some(s => s.locked);
  return (
    <div className='h-full overflow-y-auto px-6 py-8 sm:px-10'>
      <div className='mx-auto w-full max-w-[68ch]'>
        <LessonPanelHeader
          title={lessonTitle}
          meta={[
            'Podcasts',
            'Two hosts',
            `${made} of ${sections.length} recorded`,
          ]}
          className='mb-6'
        />
        <p className='text-sm leading-relaxed text-muted-foreground'>
          Each section has its own episode: two hosts talk it through. Make one
          when you want it, then listen from here or while you read.
          {anyLocked && " On Free, a lesson's first section gets a podcast."}
        </p>
        <ol className='mt-6 space-y-2' aria-label='Section podcasts'>
          {sections.map(s => (
            <SectionRow
              key={s.index}
              lessonId={lessonId}
              section={s}
              onOpen={() => onOpen(s.index)}
            />
          ))}
        </ol>
      </div>
    </div>
  );
}

function SectionRow({
  lessonId,
  section,
  onOpen,
}: {
  lessonId: string;
  section: SectionPodcastSummary;
  onOpen: () => void;
}) {
  const made = section.status === 'completed';
  return (
    <li className='flex items-center gap-3 rounded-xl border border-foreground/10 p-3 sm:p-4'>
      <button
        type='button'
        onClick={onOpen}
        disabled={!made}
        aria-label={`Section ${section.index}: ${section.title}, ${statusLine(section)}`}
        className={cn(
          'tap-target group flex min-w-0 flex-1 items-center gap-3 rounded-lg text-left',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
          !made && 'cursor-default'
        )}
      >
        <span
          className='flex h-7 w-7 shrink-0 items-center justify-center rounded-full border border-primary/40 bg-primary/10 font-mono text-xs font-medium text-foreground dark:bg-primary/15'
          aria-hidden
        >
          {section.index}
        </span>
        <span className='min-w-0 flex-1'>
          <InlineMarkdown className='block truncate font-semibold text-foreground'>
            {section.title}
          </InlineMarkdown>
          <span className='mt-0.5 flex items-center gap-1.5 font-mono text-xs text-muted-foreground'>
            {isPodcastInFlight(section.status) && (
              <Loader2
                className='h-3 w-3 animate-spin motion-reduce:animate-none'
                aria-hidden
              />
            )}
            {statusLine(section)}
          </span>
        </span>
        {made && (
          <ChevronRight
            className='h-4 w-4 shrink-0 text-muted-foreground transition-transform group-hover:translate-x-0.5'
            aria-hidden
          />
        )}
      </button>
      <SectionListenButton lessonId={lessonId} sectionIndex={section.index} />
    </li>
  );
}
